import { Component, OnInit, Input } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';

import {
  DesignationStateModel,
  DesignationActions,
  Designation
} from '../transaction-store/designation';
import { AppSettings } from '../app.settings';
import { MultiDesignationModalComponent } from '../static-section/multi-designation-modal.component';
import { Router, ActivatedRoute } from '@angular/router';
import { GaClient } from '../../init';
import { HelperService } from '../shared/helper.service';
import {
  AddOrReplaceCartDesignation,
  RemoveCartDesignation,
  SetCartDesignations
} from '../../init/idonatelib';
import { environment } from '../../environments/environment';
import { Transaction } from '../transaction-store/transaction';

@Component({
  selector: 'app-designations',
  template: `
    <section>
      <div
        id="designation-dropdown"
        class="container"
        [ngClass]="{
          'validation-failed':
            (showValidation || isTouched) &&
            !noDesignations &&
            (!firstDesignationModel ||
              !firstDesignationModel.val ||
              firstDesignationModel.val == '')
        }"
        *ngIf="
          (designations$ | async).items.length <= 1 &&
          !locked &&
          !noDesignations
        "
      >
        <ng-select
          [items]="designationOptions"
          [(ngModel)]="firstDesignationModel.val"
          [required]="true"
          [searchable]="true"
          [searchFn]="searchDesignations"
          (ngModelChange)="addDesignation($event)"
          [clearable]="false"
          placeholder="Select a designation *"
          bindLabel="title"
          bindValue="val"
          (blur)="handleBlur($event)"
        >
          <ng-template ng-label-tmp let-item="item">
            <span [title]="item.title">
              {{ item.title }}
            </span>
          </ng-template>
          <ng-template ng-option-tmp let-item="item">
            <div [title]="item.title">
              {{ item.title }}
            </div>
          </ng-template>
          <ng-template ng-footer-tmp>
            <designation-more-info
              moreInfoDesignationId="{{ firstDesignationModel.val }}"
            ></designation-more-info>
          </ng-template>
        </ng-select>
        <designation-more-info
          moreInfoDesignationId="{{ firstDesignationModel.val }}"
        ></designation-more-info>
      </div>
      <div
        class="container"
        *ngIf="allowMultides && (designations$ | async).items.length > 1"
      >
        <div class="collapsed-form">
          <div
            class="form-group row align-items-center"
            *ngFor="let des of (designations$ | async).items; let i = index"
          >
            <div class="form-control col d-flex align-items-center">
              <div class="d-inline-block mr-2">{{ des.title }}</div>
              <div class="d-inline-block mr-auto">
                {{ des.amount | currency: currencyCode:'symbol-narrow' }}
              </div>
              <i
                class="icon-closex primary-color remove-designation inline float-right"
                aria-label="Remove designation"
                (click)="removeDesignation(i)"
              ></i>
            </div>
          </div>
        </div>
      </div>
      <div
        *ngIf="allowMultides && (designations$ | async).items.length > 0"
        class="row"
      >
        <div class="container">
          <div class="col-12">
            <a
              class="float-right add-designation"
              (click)="openDesignationModal()"
              aria-label="Add another designation"
              >Add another designation</a
            >
          </div>
        </div>
      </div>
    </section>
    <section *ngIf="customDesignationLabel">
      <div class="container">
        <div class="row">
          <text-field
            class="col-12"
            type="text"
            [ngModel]="customDesignation"
            [required]="requireCustom"
            [label]="customDesignationLabel"
            (blur)="onNoteChange($event)"
          ></text-field>
        </div>
      </div>
    </section>
  `
})
export class DesignationsComponent implements OnInit {
  @Select() designations$: Observable<DesignationStateModel>;
  locked = this.settings.getValue('properties').designation_lock;
  designationOptions = [];
  selectedDesignation = {};
  allowMultides = this.settings.getValue('properties').multi_designation;
  firstDesignation: Designation;
  firstDesignationModel: any = {};
  customDesignationLabel;
  requireCustom = false;
  multiple = false;
  customDesignation: string;
  currencyCode = 'USD';
  private orderedActiveDesignationIds: string[];
  selectedDesignationId: any = null;
  featureEnabled = false;
  hasPreviewContent = false;

  focused;
  isTouched;
  @Input() showValidation = false;
  noDesignations = false;

  @Select() transaction$: Observable<Transaction>;

  ngOnInit() {
    this.transaction$.subscribe(item => {
      this.showValidation = item.validation;
    });
  }

  handleBlur($event) {
    this.isTouched = true;
  }

  generateImageUrl(image_key: string): string {
    if (!image_key) {
      return '';
    }
    if (!environment.imgServiceUrl) {
      return '';
    }
    const base_enc = {
      key: image_key,
      bucket: 'idonate-image-service',
      edits: {
        resize: {
          fit: 'outside',
          width: 300,
          height: 168
        }
      }
    };
    const encoded = btoa(JSON.stringify(base_enc));
    return environment.imgServiceUrl + encoded;
  }

  constructor(
    private store: Store,
    private modalService: NgbModal,
    private settings: AppSettings,
    private router: Router,
    route: ActivatedRoute,
    private helper: HelperService
  ) {
    this.store.dispatch(new DesignationActions.ActivateDesignation());
    const properties = this.settings.getValue('properties');
    this.currencyCode = this.settings.getValue('organization').currency_code;

    // embed-fetch/organization.designations contains designation details and is already
    // filtered down to those designations related to the embed
    const designations = this.settings.getValue('organization').designations;

    if (typeof properties.designation === 'string') {
      // .. properties.designation is potentialy from the URL
      this.selectedDesignationId = properties.designation;
    } else if (properties.designation && properties.designation.id) {
      // .. this is passed in as an object if it's coming from the Embed Properties instead of the URL
      this.selectedDesignation = properties.designation;
      this.selectedDesignationId = properties.designation.id;
    }

    const dataDesignations = properties.designations;
    if (route.snapshot.data['single']) {
      this.allowMultides = false;
    }

    const availableDesignations = designations.filter(item => item.is_active);
    if (!availableDesignations.length) {
      this.noDesignations = true;
    }

    // .. availableDesignations is actually already filtered by applied campaign as it comes from the server,
    // no need to do further filtering.

    // reformat into the format expected by ng-select
    this.designationOptions = availableDesignations.map(item => ({
      title: item.title,
      image: this.generateImageUrl(item.image_key),
      val: item.id,
      attributes: item.attributes,
      tags: item.tags,
      summary: item.summary
    }));

    if (ValidateDesignations(dataDesignations)) {
      // if multi-designation is passed in...
      const designationDispatch = [];
      for (const item of dataDesignations) {
        const desItem = this.designationOptions.find(
          des => item.id === des.val
        );
        if (desItem) {
          designationDispatch.push({
            id: item.id,
            amount: item.amount,
            title: desItem.title
          });
        }
      }
      this.store.dispatch(
        new DesignationActions.SetDesignations(designationDispatch)
      );
      SetCartDesignations(designationDispatch);
    } else if (this.selectedDesignationId) {
      // otherwise, if a single designation id was passed in... find the full designation object in designationOptions to access title
      const selectedDesignationOption = this.designationOptions.find(
        des => des.val === this.selectedDesignationId
      );

      if (selectedDesignationOption) {
        const designationObj: Designation = {
          id: selectedDesignationOption.val,
          amount: 0,
          title: selectedDesignationOption.text
        };
        this.store.dispatch(
          new DesignationActions.AddDesignation(designationObj)
        );
        // could have used ngrx for cart stuff.. but decided against.
        AddOrReplaceCartDesignation(designationObj);
      } // else, error?
    }

    // subscribe to designation changes
    this.designations$.subscribe(designations => {
      this.firstDesignation = designations.items[0];
      if (this.firstDesignation) {
        this.firstDesignationModel = {
          id: this.firstDesignation.id,
          title: this.firstDesignation.title,
          val: this.firstDesignation.id,
          designation_id: this.firstDesignation.id
        };
        this.selectedDesignationId = this.firstDesignation.id;
      }
      this.multiple = designations.items.length > 1;
      this.customDesignation = designations.custom;
      this.orderedActiveDesignationIds = designations.items.map(
        item => item.id
      );
    });
    this.customDesignationLabel = properties.custom_designation_label;
    this.requireCustom = !!parseFloat(properties.require_custom_designation);

    // capture url param
    const designation_note = properties.designation_note;
    if (designation_note && this.customDesignationLabel) {
      this.store.dispatch(new DesignationActions.SetCustomDesignation(designation_note));
    }
  }

  searchDesignations(search, item) {
    if (!search) {
      return true;
    }

    const searchMatch = new RegExp(search, 'i');

    if (item.title.match(searchMatch)) {
      return true;
    }

    for (const attr of item.attributes) {
      if (attr.searchable) {
        if (attr.value.match(searchMatch) || attr.code.match(searchMatch)) {
          return true;
        }
      }
    }
    if (item.tags && item.tags.match(searchMatch)) {
      return true;
    }
  }

  addDesignation($event) {
    const selected = this.designationOptions.find(item => $event == item.val);
    this.selectedDesignationId = $event;
    this.selectedDesignation = selected;

    if (selected) {
      const designationObj = {
        id: selected.val,
        amount: 0,
        title: selected.title,
        designation_id: selected.val,
        val: selected.val
      };
      if (this.firstDesignation) {
        this.store.dispatch(
          new DesignationActions.ReplaceDesignation({
            toReplace: this.firstDesignationModel,
            toUse: designationObj
          })
        );
      } else {
        this.store.dispatch(
          new DesignationActions.AddDesignation(designationObj)
        );
      }
      AddOrReplaceCartDesignation(designationObj);
    } // maybe there should be a removeDesignation(0) here?
  }
  convertToSelect(item) {
    return { text: item.title, val: item.id };
  }
  openDesignationModal() {
    GaClient.trackLinkClick('Open Multi-Designation');

    const modalRef = this.modalService.open(MultiDesignationModalComponent);
    this.helper.repositionModal('.add-designation');
  }
  removeDesignation(index) {
    // orderedActiveDesignationIds will be updated by RemoveDesignation side effects
    const designationId = this.orderedActiveDesignationIds[index];
    this.store.dispatch(new DesignationActions.RemoveDesignation(index));
    this.selectedDesignationId = null;

    RemoveCartDesignation({ id: designationId, amount: 0, title: '' });
  }
  onNoteChange(value) {
    this.store.dispatch(new DesignationActions.SetCustomDesignation(value));
  }
}

function ValidateDesignations(designations) {
  let valid = true;
  if (!Array.isArray(designations)) {
    valid = false;
  }
  if (valid && designations && designations.length > 0) {
    for (const designation of designations as Designation[]) {
      if (!designation.id || !designation.amount) {
        valid = false;
      }
    }
  }
  return valid;
}
